/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, asset } from "../lib/api";
import { AlertTriangle, Trophy, Star } from "lucide-react";
import { CoverReel } from "../components/CoverReel";

const META = {
  "hurricane-helene": {
    title: "Hurricane Helene",
    overline: "Documentary collection",
    icon: AlertTriangle,
    blurb: "Images and video captured during and after the storm. Some of this footage is unedited and shown as shot — a raw record of the day.",
  },
  "major-events": {
    title: "Major Events",
    overline: "Events I've covered",
    icon: Trophy,
    blurb: "A selection of the major events I've photographed — concerts, championships, rodeos, fights and headline moments in Las Vegas and beyond. New events added regularly.",
  },
};

export default function Collection() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = META[slug] || { title: slug, overline: "Collection", icon: AlertTriangle, blurb: "" };
  const Icon = meta.icon;

  useEffect(() => {
    document.title = `${meta.title} — Scott Burton Photography`;
    api.get(`/collections/${slug}/items`).then(({ data }) => { setItems(data); setLoading(false); });
  }, [slug]);

  const featured = items.filter((it) => it.featured);
  const rest = items.filter((it) => !it.featured);

  const Media = ({ it, tall }) => (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-black">
      {it.media_type === "video" ? (
        <video src={asset(it.url)} controls playsInline className={`w-full bg-black ${tall ? "max-h-[80vh]" : "max-h-[70vh]"}`} />
      ) : (
        <img src={asset(it.url)} alt={it.title} loading="lazy" className="w-full object-cover" />
      )}
    </div>
  );

  const Caption = ({ it }) => (
    <div className="mt-3 flex items-start justify-between gap-4">
      <div>
        {it.title && <h3 className="font-serif text-2xl font-light">{it.title}</h3>}
        {it.description && <p className="text-neutral-400 text-sm mt-1 leading-relaxed">{it.description}</p>}
      </div>
      {!it.edited && <span className="shrink-0 font-mono text-[0.6rem] tracking-wider border border-white/15 rounded-full px-2 py-1 text-neutral-400">UNEDITED</span>}
    </div>
  );

  const useReel = slug === "major-events" && !loading && featured.length > 0;

  return (
    <div>
      {useReel && (
        <CoverReel
          variant="event"
          icon={Trophy}
          overline={meta.overline}
          heading={meta.title}
          blurb={meta.blurb}
          slides={featured.map((it) => ({ id: it.id, src: asset(it.url), title: it.title, caption: it.description }))}
        />
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" style={{ paddingTop: useReel ? "3.5rem" : "7rem" }}>
        {!useReel && (
          <>
            <div className="overline-label mb-3 flex items-center gap-2"><Icon size={13} /> {meta.overline}</div>
            <h1 className="font-serif text-5xl lg:text-7xl font-light">{meta.title}</h1>
            <p className="mt-5 text-neutral-400 max-w-2xl">{meta.blurb}</p>
          </>
        )}

        {loading ? (
          <div className="py-32 text-center text-neutral-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-32 text-center text-neutral-500">This collection is empty. Add media from the Studio Admin.</div>
        ) : (
          <>
            {featured.length > 0 && !useReel && (
              <div className="mt-14" data-testid="collection-featured">
                <div className="overline-label mb-6 flex items-center gap-2 text-amber-glow"><Star size={12} fill="currentColor" /> Featured</div>
                <div className="grid gap-8 md:grid-cols-2">
                  {featured.map((it, i) => (
                    <div key={it.id} data-testid={`collection-featured-${it.id}`} className="animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                      <Media it={it} tall />
                      <Caption it={it} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className={featured.length > 0 && !useReel ? "mt-16" : ""}>
              {featured.length > 0 && !useReel && <div className="overline-label mb-6 text-neutral-500">All events</div>}
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {(useReel ? items : rest).map((it, i) => (
                  <div key={it.id} data-testid={`collection-item-${it.id}`} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                    <Media it={it} />
                    <Caption it={it} />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
